import { useState, useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { authApi } from '../services/api'

export default function Login() {
  const [searchParams] = useSearchParams()

  // Store pending plan from URL params (for subscribe flow)
  useEffect(() => {
    const plan = searchParams.get('plan')
    if (plan) {
      sessionStorage.setItem('pending_plan', plan)
    }
  }, [searchParams])
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [showOtpVerification, setShowOtpVerification] = useState(false)
  const [pendingUserData, setPendingUserData] = useState(null)
  const [otp, setOtp] = useState(['', '', '', '', '', ''])
  const [showScrapingProgress, setShowScrapingProgress] = useState(false)
  const [scrapingStatus, setScrapingStatus] = useState(null)
  const [scrapingJobId, setScrapingJobId] = useState(null)
  const pollingRef = useRef(null)
  const { login } = useAuth()

  // Poll scraping status when showing progress
  useEffect(() => {
    if (showScrapingProgress && scrapingJobId) {
      const pollStatus = async () => {
        try {
          const isDev = import.meta.env.DEV || window.location.hostname === 'localhost'
          const baseUrl = isDev ? '' : (import.meta.env.VITE_API_URL?.replace(/\/api(\/v1)?$/, '') || 'https://api.cognavia.ai')
          const response = await fetch(`${baseUrl}/api/auth/register/scraping-status/${scrapingJobId}`)
          const data = await response.json()
          setScrapingStatus(data)

          if (data.status === 'completed') {
            clearInterval(pollingRef.current)
            sessionStorage.removeItem('scraping_job_id')
            setTimeout(() => {
              window.location.assign('/customer-dashboard?tab=api-keys')
            }, 1500)
          } else if (data.status === 'failed') {
            clearInterval(pollingRef.current)
            sessionStorage.removeItem('scraping_job_id')
          }
        } catch (err) {
          console.error('Failed to poll scraping status:', err)
        }
      }

      pollStatus()
      pollingRef.current = setInterval(pollStatus, 3000)

      return () => {
        if (pollingRef.current) clearInterval(pollingRef.current)
      }
    }
  }, [showScrapingProgress, scrapingJobId])

  // Handle login form submission
  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!email || !password) {
      setError('Please fill in all fields')
      return
    }

    setLoading(true)
    setError('')

    try {
      const result = await login(email, password)

      if (result.success) {
        // Handover-agent sub-users bypass all customer-only flows (scraping,
        // subscribe gating) and go straight to the dedicated takeover console.
        if (result.user?.role === 'handover_agent') {
          window.location.assign(result.redirect || '/agent-dashboard')
          return
        }

        if (result.requires_verification) {
          setPendingUserData(result.userData)
          setShowOtpVerification(true)
        } else if (result.setup_mode === 'simple') {
          // Simple setup referral user - check for active scraping
          const jobId = result.scraping_job_id || sessionStorage.getItem('scraping_job_id')
          if (jobId) {
            setScrapingJobId(jobId)
            setShowScrapingProgress(true)
          } else {
            // Check for pending plan redirect
            const pendingPlan = sessionStorage.getItem('pending_plan')
            if (pendingPlan) {
              window.location.assign(`/subscribe?plan=${pendingPlan}`)
            } else {
              window.location.assign('/customer-dashboard')
            }
          }
        } else {
          // Check for pending plan redirect
          const pendingPlan = sessionStorage.getItem('pending_plan')
          if (pendingPlan) {
            window.location.assign(`/subscribe?plan=${pendingPlan}`)
          } else {
            window.location.assign('/customer-dashboard')
          }
        }
      } else {
        setError(result.error || 'Login failed')
      }
    } catch (error) {
      console.error('Login error:', error)
      setError('An error occurred during login')
    } finally {
      setLoading(false)
    }
  }

  // Handle OTP input
  const handleOtpChange = (index, value) => {
    if (value.length <= 1) {
      const newOtp = [...otp]
      newOtp[index] = value
      setOtp(newOtp)

      if (value && index < 5) {
        const nextInput = document.querySelector(`input[name="otp-${index + 1}"]`)
        if (nextInput) nextInput.focus()
      }
    }
  }

  // Handle OTP paste
  const handleOtpPaste = (e) => {
    e.preventDefault()
    const paste = e.clipboardData.getData('text')
    if (paste.length === 6 && /^\d{6}$/.test(paste)) {
      const newOtp = paste.split('')
      setOtp(newOtp)
      setTimeout(() => handleOtpSubmit(newOtp), 100)
    }
  }

  // Submit OTP
  const handleOtpSubmit = async (otpArray = otp) => {
    const otpCode = otpArray.join('')
    if (otpCode.length !== 6) {
      setError('Please enter the complete 6-digit code')
      return
    }

    setLoading(true)
    setError('')

    try {
      const response = await authApi.verifyOtp({
        email: pendingUserData.email,
        code: otpCode
      })

      if (response.success) {
        localStorage.setItem('auth_token', response.access_token)
        localStorage.setItem('user_data', JSON.stringify(response.user))
        const pendingPlan = sessionStorage.getItem('pending_plan')
        if (pendingPlan) {
          window.location.assign(`/subscribe?plan=${pendingPlan}`)
        } else {
          window.location.assign('/customer-dashboard')
        }
      } else {
        setError(response.message || 'Invalid verification code')
      }
    } catch (error) {
      console.error('OTP verification error:', error)
      setError('Verification failed. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  // Get stage display text
  const getStageText = () => {
    if (!scrapingStatus) return 'Initializing...'
    switch (scrapingStatus.stage) {
      case 'Starting...': return 'Starting website scan...'
      case 'scraping': return 'Scanning your website...'
      case 'processing': return 'Processing content...'
      case 'embedding': return 'Building knowledge base...'
      case 'indexing': return 'Indexing for AI responses...'
      case 'completed': return 'Setup complete!'
      default: return scrapingStatus.stage || 'Processing...'
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Scraping Progress Screen */}
        {showScrapingProgress && (
          <div className="text-center space-y-6">
            <div>
              <h2 className="text-3xl font-extrabold text-gray-900 mb-2">Setting Up Your Account</h2>
              <p className="text-gray-600">We're scanning your website and building your AI knowledge base</p>
            </div>

            <div className="bg-white rounded-xl shadow-md p-8 space-y-6">
              {scrapingStatus?.status === 'failed' ? (
                <>
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto">
                    <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">Setup encountered an issue</h3>
                    <p className="text-sm text-gray-600 mb-4">
                      {scrapingStatus.error || 'Website scanning could not be completed. You can set up manually from your dashboard.'}
                    </p>
                  </div>
                  <button
                    onClick={() => window.location.assign('/customer-dashboard')}
                    className="w-full py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
                  >
                    Continue to Dashboard
                  </button>
                </>
              ) : scrapingStatus?.status === 'completed' ? (
                <>
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                    <svg className="w-8 h-8 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">Setup Complete!</h3>
                    <p className="text-sm text-gray-600">
                      {scrapingStatus.chunks_count ? `${scrapingStatus.chunks_count} content sections indexed. ` : ''}
                      Redirecting to your dashboard...
                    </p>
                  </div>
                  <div className="flex justify-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                  </div>
                </>
              ) : (
                <>
                  {/* Animated spinner */}
                  <div className="flex justify-center">
                    <div className="relative">
                      <div className="w-20 h-20 border-4 border-indigo-200 rounded-full"></div>
                      <div className="absolute top-0 left-0 w-20 h-20 border-4 border-indigo-600 rounded-full border-t-transparent animate-spin"></div>
                    </div>
                  </div>

                  {/* Progress bar */}
                  <div>
                    <div className="flex justify-between text-sm text-gray-600 mb-1">
                      <span>{getStageText()}</span>
                      <span>{scrapingStatus?.progress || 0}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-indigo-600 h-2 rounded-full transition-all duration-500"
                        style={{ width: `${scrapingStatus?.progress || 0}%` }}
                      ></div>
                    </div>
                  </div>

                  <p className="text-xs text-gray-500">This may take a minute. Please don't close this page.</p>
                </>
              )}
            </div>

            {/* Professional Attribution */}
            <div className="pt-4 border-t border-gray-200">
              <p className="text-xs text-center text-gray-500">
                Powered by{' '}
                <a href="https://cognavia.ai" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700 font-medium">
                  CognaAssist™
                </a>
                {' '}by Cognavia.ai
              </p>
            </div>
          </div>
        )}

        {/* Regular Login / OTP Views */}
        {!showScrapingProgress && (
          <>
            <div>
              <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
                {showOtpVerification ? 'Verify Your Email' : 'Sign in to your account'}
              </h2>
            </div>

            {/* Regular Login Form */}
            {!showOtpVerification && (
              <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
                <div className="rounded-md shadow-sm -space-y-px">
                  <div>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Email address"
                      className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                      required
                    />
                  </div>
                  <div>
                    <input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Password"
                      className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                      required
                    />
                  </div>
                </div>

                {error && (
                  <div className="rounded-md bg-red-50 p-4">
                    <div className="text-sm text-red-700">{error}</div>
                  </div>
                )}

                <div>
                  <button
                    type="submit"
                    disabled={loading}
                    className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                  >
                    {loading ? 'Signing in...' : 'Sign in'}
                  </button>
                </div>

                <div className="text-center space-y-2">
                  <div>
                    <a href="/forgot-password" className="text-indigo-600 hover:text-indigo-500 text-sm font-medium">
                      Forgot your password?
                    </a>
                  </div>
                  <div>
                    <span className="text-sm text-gray-600">Don't have an account? </span>
                    <a href="/register" className="text-indigo-600 hover:text-indigo-500 text-sm font-medium">
                      Sign up
                    </a>
                  </div>
                </div>

                {/* Professional Attribution */}
                <div className="pt-4 border-t border-gray-200">
                  <p className="text-xs text-center text-gray-500">
                    Powered by{' '}
                    <a href="https://cognavia.ai" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700 font-medium">
                      CognaAssist™
                    </a>
                    {' '}by Cognavia.ai
                  </p>
                </div>
              </form>
            )}

            {/* OTP Verification Form */}
            {showOtpVerification && (
              <div className="mt-8 space-y-6">
                <div className="text-center">
                  <p className="text-sm text-gray-600">
                    We've sent a 6-digit verification code to <strong>{pendingUserData?.email}</strong>
                  </p>
                </div>

                <div className="flex justify-center space-x-2">
                  {otp.map((digit, index) => (
                    <input
                      key={index}
                      type="text"
                      name={`otp-${index}`}
                      value={digit}
                      onChange={(e) => handleOtpChange(index, e.target.value)}
                      onPaste={index === 0 ? handleOtpPaste : undefined}
                      className="w-12 h-12 text-center border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-lg font-medium"
                      maxLength="1"
                    />
                  ))}
                </div>

                {error && (
                  <div className="rounded-md bg-red-50 p-4">
                    <div className="text-sm text-red-700">{error}</div>
                  </div>
                )}

                <button
                  onClick={() => handleOtpSubmit()}
                  disabled={loading}
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                >
                  {loading ? 'Verifying...' : 'Verify Code'}
                </button>

                <div className="text-center">
                  <button
                    onClick={() => {
                      setShowOtpVerification(false)
                      setPendingUserData(null)
                      setOtp(['', '', '', '', '', ''])
                      setError('')
                    }}
                    className="text-indigo-600 hover:text-indigo-500 text-sm"
                  >
                    Back to Login
                  </button>
                </div>

                {/* Professional Attribution */}
                <div className="pt-4 border-t border-gray-200">
                  <p className="text-xs text-center text-gray-500">
                    Powered by{' '}
                    <a href="https://cognavia.ai" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700 font-medium">
                      CognaAssist™
                    </a>
                    {' '}by Cognavia.ai
                  </p>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
